/**
 * Builds server-side filter variables from local filter state.
 *
 * Extracted from AllTargetsTableContainer to keep the component under
 * the cognitive complexity and max-lines thresholds. Uses spread
 * composition so each helper returns either a single-key object
 * (active filter) or empty object (omitted), staying fully immutable.
 * @module features/scouting/utils/buildFilterVariables
 */

import type { CustomStatFilterInput } from "@/generated/graphql";

import {
  DEFAULT_AGE_RANGE,
  DEFAULT_CONTRACT_MONTHS_RANGE,
  DEFAULT_CUSTOM_STAT_RANGE,
  DEFAULT_SCORE_RANGE,
  DEFAULT_SCORE_RANGE_DECIMAL,
  DEFAULT_VALUATION_RANGE,
  isRangeActive,
} from "@/constants/filterDefaults";

/**
 * Spreads a range filter entry when the range differs from defaults.
 * @param key - Filter key name in the query variables
 * @param range - Current [min, max] range tuple
 * @param defaultRange - Default [min, max] range for comparison
 * @returns Single-key object with min/max when active, empty object otherwise
 */
const spreadRange = (
  key: string,
  range: [number, number],
  defaultRange: [number, number]
): Record<string, { min: number; max: number }> =>
  isRangeActive(range, defaultRange)
    ? { [key]: { min: range[0], max: range[1] } }
    : {};

/**
 * Spreads a range into flat minX/maxX keys for PlayerAdvancedFilterInput.
 * @param prefix - Field prefix (e.g. "Age" → minAge/maxAge)
 * @param range - Current [min, max] range tuple
 * @param defaultRange - Default [min, max] range for comparison
 * @returns Object with minX/maxX keys when active, empty object otherwise
 */
const spreadFlatRange = (
  prefix: string,
  range: [number, number],
  defaultRange: [number, number]
): Record<string, number> =>
  isRangeActive(range, defaultRange)
    ? {
        [`min${prefix}`]: range[0],
        [`max${prefix}`]: range[1],
      }
    : {};

/**
 * Scales a 0-100 UI range to 0-1 decimal for fitScore backend comparison.
 * The DB stores fitScore as a 0-1 decimal, but the UI operates on 0-100.
 * @param range - UI-scale [min, max] tuple (0-100)
 * @returns Decimal-scale [min, max] tuple (0-1)
 */
const scaleFitScore = (range: [number, number]): [number, number] => [
  range[0] / 100,
  range[1] / 100,
];

/**
 * Spreads a non-empty ID array as a single-key filter entry.
 * @param key - Filter key name in the query variables
 * @param ids - Array of selected IDs
 * @returns Single-key object with the IDs when non-empty, empty object otherwise
 */
const spreadIds = (
  key: string,
  ids: readonly string[]
): Record<string, readonly string[]> => (ids.length > 0 ? { [key]: ids } : {});

/** Filter state inputs for building server-side query variables */
export interface FilterInputs {
  readonly debouncedSearch: string;
  readonly selectedPositionIds: readonly string[];
  readonly selectedClubIds: readonly string[];
  readonly selectedWatchlistId: string | null;
  /** Scout IDs filter — only available on web (desktop) */
  readonly selectedScoutIds?: readonly string[];
  readonly selectedNationalityIds: readonly string[];
  readonly selectedLeagueIds: readonly string[];
  readonly selectedCompetitionIds?: readonly string[];
  readonly selectedRoleArchetypes: readonly string[];
  readonly ageRange: [number, number];
  readonly contractMonthsRange: [number, number];
  readonly valuationRange: [number, number];
  readonly gprRange: [number, number];
  readonly fitScoreRange: [number, number];
  readonly teamStyleFitRange: [number, number];
  readonly carryingRange: [number, number];
  readonly dribblingRange: [number, number];
  readonly passingRange: [number, number];
  readonly shootingRange: [number, number];
  readonly gkPassingRange: [number, number];
  readonly gkShotStoppingRange: [number, number];
  readonly gkPositioningRange: [number, number];
  readonly customStatRanges: Readonly<
    Record<string, readonly [number, number]>
  >;
  readonly filterByOrganizationRoster?: boolean;
  /**
   * Active team scope from `useTeamSelection`. When set alongside
   * `filterByOrganizationRoster: true`, the backend narrows the org roster
   * to this team. `null` in hidden-mode orgs and when no team is picked,
   * which preserves the existing org-wide behavior. (SE-4931)
   */
  readonly activeTeamId?: string | null;
}

/**
 * Converts customStatRanges into the GraphQL filterByCustomStats array.
 * Only includes ranges that differ from the default [0, 10].
 * @param ranges - Custom stat ranges keyed by stat name
 * @returns Array of CustomStatFilterInput entries for active filters
 */
const buildCustomStatFilters = (
  ranges: Readonly<Record<string, readonly [number, number]>>
): readonly CustomStatFilterInput[] =>
  Object.entries(ranges)
    .filter(([, range]) =>
      isRangeActive(range as [number, number], DEFAULT_CUSTOM_STAT_RANGE)
    )
    .map(([name, range]) => ({
      statName: name,
      minValue: range[0],
      maxValue: range[1],
    }));

/**
 * Builds the server-side filter variables from local filter state.
 * Omits keys whose value matches the default so the query only sends
 * active filters.
 * @param inputs - All filter state values
 * @returns Filter variables object with only active filter keys populated
 */
const buildFilterVariables = (
  inputs: FilterInputs
): Record<string, unknown> => {
  const customStatFilters = buildCustomStatFilters(inputs.customStatRanges);
  return {
    ...(inputs.debouncedSearch ? { search: inputs.debouncedSearch } : {}),
    ...spreadIds("filterByPositionIds", inputs.selectedPositionIds),
    ...spreadIds("filterByTeamIds", inputs.selectedClubIds),
    ...(inputs.selectedWatchlistId
      ? { filterByWatchlistIds: [inputs.selectedWatchlistId] }
      : {}),
    ...spreadIds("filterByScoutIds", inputs.selectedScoutIds ?? []),
    ...spreadIds("filterByNationalityIds", inputs.selectedNationalityIds),
    ...spreadIds("filterByLeagueIds", inputs.selectedLeagueIds),
    ...spreadIds("filterByRoleArchetypes", inputs.selectedRoleArchetypes),
    ...spreadRange("age", inputs.ageRange, DEFAULT_AGE_RANGE),
    ...spreadRange(
      "contractMonths",
      inputs.contractMonthsRange,
      DEFAULT_CONTRACT_MONTHS_RANGE
    ),
    // SE-6496: the Cost filter binds the GPV midpoint, not the legacy
    // Transfermarkt valuation. Players without a GPV are excluded server-side
    // while a bound is active.
    ...spreadRange(
      "geminiPlayerValuation",
      inputs.valuationRange,
      DEFAULT_VALUATION_RANGE
    ),
    ...spreadRange("gpr", inputs.gprRange, DEFAULT_SCORE_RANGE),
    ...spreadRange(
      "fitScore",
      scaleFitScore(inputs.fitScoreRange),
      DEFAULT_SCORE_RANGE_DECIMAL
    ),
    ...spreadRange(
      "teamStyleFit",
      inputs.teamStyleFitRange,
      DEFAULT_SCORE_RANGE
    ),
    ...spreadRange("carrying", inputs.carryingRange, DEFAULT_SCORE_RANGE),
    ...spreadRange("dribbling", inputs.dribblingRange, DEFAULT_SCORE_RANGE),
    ...spreadRange("passing", inputs.passingRange, DEFAULT_SCORE_RANGE),
    ...spreadRange("shooting", inputs.shootingRange, DEFAULT_SCORE_RANGE),
    ...spreadRange("gkPassing", inputs.gkPassingRange, DEFAULT_SCORE_RANGE),
    ...spreadRange(
      "gkShotStopping",
      inputs.gkShotStoppingRange,
      DEFAULT_SCORE_RANGE
    ),
    ...spreadRange(
      "gkPositioning",
      inputs.gkPositioningRange,
      DEFAULT_SCORE_RANGE
    ),
    ...(customStatFilters.length > 0
      ? { filterByCustomStats: customStatFilters }
      : {}),
    ...(inputs.filterByOrganizationRoster
      ? { filterByOrganizationRoster: true }
      : {}),
    ...(inputs.activeTeamId ? { teamId: inputs.activeTeamId } : {}),
  };
};

/**
 * Builds a PlayerAdvancedFilterInput-compatible object from filter state.
 * Uses flat minX/maxX keys instead of nested { min, max } objects.
 * @param inputs - All filter state values
 * @returns Object matching the PlayerAdvancedFilterInput GraphQL type
 */
export const buildAdvancedFilterInput = (
  inputs: FilterInputs
): Record<string, unknown> => {
  const customStatFilters = buildCustomStatFilters(inputs.customStatRanges);
  return {
    ...(inputs.selectedPositionIds.length > 0
      ? { positionIds: inputs.selectedPositionIds }
      : {}),
    ...(inputs.selectedClubIds.length > 0
      ? { teamIds: inputs.selectedClubIds }
      : {}),
    ...(inputs.selectedNationalityIds.length > 0
      ? { nationalities: inputs.selectedNationalityIds }
      : {}),
    ...(inputs.selectedLeagueIds.length > 0
      ? { leagueIds: inputs.selectedLeagueIds }
      : {}),
    ...((inputs.selectedCompetitionIds?.length ?? 0) > 0
      ? { competitionIds: inputs.selectedCompetitionIds }
      : {}),
    ...(inputs.selectedRoleArchetypes.length > 0
      ? { roleArchetypes: inputs.selectedRoleArchetypes }
      : {}),
    ...spreadFlatRange("Age", inputs.ageRange, DEFAULT_AGE_RANGE),
    ...spreadFlatRange(
      "MonthsRemaining",
      inputs.contractMonthsRange,
      DEFAULT_CONTRACT_MONTHS_RANGE
    ),
    // SE-6496: the Cost filter binds the GPV midpoint, not the legacy
    // Transfermarkt valuation. Players without a GPV are excluded server-side
    // while a bound is active.
    ...spreadFlatRange(
      "GeminiPlayerValuation",
      inputs.valuationRange,
      DEFAULT_VALUATION_RANGE
    ),
    ...spreadFlatRange("Gpr", inputs.gprRange, DEFAULT_SCORE_RANGE),
    ...spreadFlatRange(
      "FitScore",
      scaleFitScore(inputs.fitScoreRange),
      DEFAULT_SCORE_RANGE_DECIMAL
    ),
    ...spreadFlatRange(
      "TeamStyleFit",
      inputs.teamStyleFitRange,
      DEFAULT_SCORE_RANGE
    ),
    ...spreadFlatRange("Carrying", inputs.carryingRange, DEFAULT_SCORE_RANGE),
    ...spreadFlatRange("Dribbling", inputs.dribblingRange, DEFAULT_SCORE_RANGE),
    ...spreadFlatRange("Passing", inputs.passingRange, DEFAULT_SCORE_RANGE),
    ...spreadFlatRange("Shooting", inputs.shootingRange, DEFAULT_SCORE_RANGE),
    ...spreadFlatRange("GkPassing", inputs.gkPassingRange, DEFAULT_SCORE_RANGE),
    ...spreadFlatRange(
      "GkShotStopping",
      inputs.gkShotStoppingRange,
      DEFAULT_SCORE_RANGE
    ),
    ...spreadFlatRange(
      "GkPositioning",
      inputs.gkPositioningRange,
      DEFAULT_SCORE_RANGE
    ),
    ...(customStatFilters.length > 0 ? { customStats: customStatFilters } : {}),
  };
};

export default buildFilterVariables;
